// iter199 — Compact Excel-style Asset Handover reconciliation UI.
// Rebuilt for fast physical reconciliation of 400+ item exits.
//
// Design intent:
//   • Flat compact table, NOT cards — dense rows so 20+ items visible at once.
//   • Global search (item / category / location / source / asset id).
//   • Quick filters: ALL / PENDING / VERIFIED / DIFFERENCE / PRICE ISSUES
//     + category chips (FOOD / KITCHEN / FURNITURE / PACKAGING / OTHER).
//   • Counters row: Total / Verified / Matched / Short / Excess / Pending
//                    + Price Missing / Mismatch / Manual.
//   • "COPY SYSTEM QTY → PHYSICAL QTY" as the fastest default workflow —
//     applies to selected rows, current filtered view, or entire category.
//   • Bulk-action bar: Copy System Qty · Condition:Good · Verified · Accept
//                       + Add Remark · Clear selection.
//   • Row-level Physical Qty input auto-advances to next on Enter (rapid keys).
//   • Debounced auto-save per row (500 ms).  No individual Save buttons.
//   • Smart-remarks: unchanged qty + Good condition → remark optional.  Any
//     mismatch OR damaged/broken/missing → remark required (row highlights).
//   • Exit Unit Rate override — inline editor visible ONLY for food-inventory
//     rows with PRICE MISSING / PRICE MISMATCH / MANUAL / user-selected.
//     Reason required before save.  Never overwrites historical WAP.
//   • Final review dialog — SIGNED / PENDING / SHORT / EXCESS / DAMAGED counts
//     with per-row exceptions.  Blocks Finalize if any food row has physical
//     qty > 0 but no valid Exit Unit Rate.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle, Trash2, Search, ArrowRightLeft, AlertTriangle, ChevronRight, ChevronDown, Copy, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CONDITIONS = ['New', 'Good', 'Fair', 'Repair Required', 'Damaged', 'Missing', 'Scrap'];
const CONDITIONS_NEEDS_REMARK = new Set(['Repair Required', 'Damaged', 'Missing', 'Scrap']);
const QUICK_REMARKS = ['Broken', 'Missing', 'Damaged', 'Disposed during operations', 'Normal operational breakage', 'Quantity variance', 'Other'];
const EXIT_HEAD_META = {
  kitchen_equipment:   { label: 'Kitchen Equipment', icon: '🍳', filter: 'KITCHEN' },
  furniture_fixtures:  { label: 'Furniture & Fixtures', icon: '🪑', filter: 'FURNITURE' },
  utensils_machinery:  { label: 'Utensils & Machinery', icon: '🔧', filter: 'KITCHEN' },
  service_electrical:  { label: 'Service & Electrical', icon: '⚡', filter: 'OTHER' },
  food_inventory:      { label: 'Food Inventory', icon: '🍲', filter: 'FOOD' },
  packaging_materials: { label: 'Packaging Materials', icon: '📦', filter: 'PACKAGING' },
  other_assets:        { label: 'Other Assets', icon: '📋', filter: 'OTHER' },
  unclassified:        { label: 'Unclassified — Review', icon: '❓', filter: 'OTHER' },
};
const RATE_REASONS = [
  'Latest purchase invoice',
  'Physical verification',
  'Vendor confirmation',
  'Current purchase rate',
  'System price incorrect',
  'Other',
];

// —— util ————————————————————————————————————————
function useDebouncedCallback(fn, delay) {
  const timerRef = useRef(null);
  return useCallback((...args) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

function inr(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// —— main component ————————————————————————————————
export default function AssetHandoverCompact({ exitId, token, session, onClose, onSaved }) {
  const isSA = !!session?.is_super_admin;
  const canOverrideRate = isSA || !!session?.is_admin
    || !!session?.roles?.accounting || !!session?.roles?.accounts
    || !!session?.roles?.finance
    || ['accountant','cfo','finance'].includes((session?.role_key || '').toLowerCase());

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [lastEvent, setLastEvent] = useState(null);
  const [dirtyRow, setDirtyRow] = useState({});         // sid → patch
  const [savingRow, setSavingRow] = useState({});       // sid → true
  const [savedRow, setSavedRow] = useState({});         // sid → ts
  const [selected, setSelected] = useState(new Set());  // sids
  const [query, setQuery] = useState('');
  const [quickFilter, setQuickFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [collapsed, setCollapsed] = useState({});
  const [priceEditRow, setPriceEditRow] = useState(null); // {sid, reason, rate}
  const [reviewOpen, setReviewOpen] = useState(false);
  // iter220 — Physical-qty filter chip + sort + finalize-blockers list.
  const [qtyFilter, setQtyFilter] = useState('ALL'); // ALL | ZERO | GT_ZERO | DIFFERENCE | SHORT | EXCESS | MATCHED
  const [sortBy, setSortBy] = useState(null);        // 'physical' | 'system' | 'diff' | 'exit_rate' | 'value' | 'item' | 'category'
  const [sortDir, setSortDir] = useState('asc');
  const [bulkRateOpen, setBulkRateOpen] = useState(false);
  const [bulkRate, setBulkRate] = useState({ source: 'SOURCE_CURRENT', rate: '', reason: '' });
  const [blockerSids, setBlockerSids] = useState([]); // finalize hard-blocker list
  const [showOnlyBlockers, setShowOnlyBlockers] = useState(false);
  const qtyRefs = useRef({});                            // sid → HTMLInputElement

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/list/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.detail || 'Load failed');
      // Flatten grouped → sortable list preserving head order.
      const flat = [];
      (d.heads_ordered || []).forEach(h => (d.grouped[h] || []).forEach(row => flat.push(row)));
      setRows(flat);
      setTotals(d.totals || {});
      setLastEvent(d.last_snapshot_event || null);
    } catch (e) {
      toast.error(e.message || 'Failed to load handover');
    } finally { setLoading(false); }
  }, [exitId, token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // —— save (single row, debounced) ————————————————
  const saveRowNow = useCallback(async (sid, patch) => {
    setSavingRow(p => ({ ...p, [sid]: true }));
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/upsert-row/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, snapshot_item_id: sid, ...patch }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setSavedRow(p => ({ ...p, [sid]: Date.now() }));
      setDirtyRow(p => { const n = { ...p }; delete n[sid]; return n; });
      // patch local row in place so counters + filter update without a full refetch
      setRows(prev => prev.map(x => {
        if (x.snapshot_item_id !== sid) return x;
        return { ...x, ...patch };
      }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingRow(p => { const n = { ...p }; delete n[sid]; return n; });
    }
  }, [exitId, token]);

  const debouncedSave = useDebouncedCallback((sid, patch) => saveRowNow(sid, patch), 500);

  const patchRow = (sid, field, value) => {
    setDirtyRow(p => {
      const next = { ...p, [sid]: { ...(p[sid] || {}), [field]: value } };
      debouncedSave(sid, next[sid]);
      return next;
    });
    // Optimistic local update so UI/filter/counters react immediately.
    setRows(prev => prev.map(x => x.snapshot_item_id === sid ? { ...x, [field]: value } : x));
  };

  // —— filtering ————————————————————————————————
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const blockerSet = new Set(blockerSids);
    let arr = rows.filter(r => {
      // iter220 — "only show blockers" mode (from finalize-error click)
      if (showOnlyBlockers && !blockerSet.has(r.snapshot_item_id)) return false;
      // category chip
      const meta = EXIT_HEAD_META[r.exit_head] || { filter: 'OTHER' };
      if (categoryFilter !== 'ALL' && meta.filter !== categoryFilter) return false;
      // quick filter
      const sq = Number(r.quantity_system ?? 0);
      const pq = r.quantity_physical == null ? null : Number(r.quantity_physical);
      const diff = pq == null ? null : pq - sq;
      const isVerified = pq != null;
      const isMatch   = isVerified && Math.abs(diff) < 1e-6;
      const isDiff    = isVerified && !isMatch;
      const isPending = !isVerified;
      const isPriceIssue = r.price_status === 'MISSING' || r.price_status === 'MISMATCH';
      if (quickFilter === 'PENDING'    && !isPending)   return false;
      if (quickFilter === 'VERIFIED'   && !isVerified)  return false;
      if (quickFilter === 'DIFFERENCE' && !isDiff)      return false;
      if (quickFilter === 'MATCHED'    && !isMatch)     return false;
      if (quickFilter === 'PRICE'      && !isPriceIssue) return false;
      if (quickFilter === 'PRICE_MISSING'  && r.price_status !== 'MISSING') return false;
      if (quickFilter === 'PRICE_MISMATCH' && r.price_status !== 'MISMATCH') return false;
      if (quickFilter === 'MANUAL_PRICE'   && r.price_status !== 'MANUAL') return false;
      // iter220 — physical-qty filter chip
      if (qtyFilter === 'ZERO' && !(pq !== null && pq === 0)) return false;
      if (qtyFilter === 'GT_ZERO' && !(pq !== null && pq > 0)) return false;
      if (qtyFilter === 'DIFFERENCE' && !isDiff) return false;
      if (qtyFilter === 'SHORT' && !(isVerified && diff < 0)) return false;
      if (qtyFilter === 'EXCESS' && !(isVerified && diff > 0)) return false;
      if (qtyFilter === 'MATCHED' && !isMatch) return false;
      // search
      if (q) {
        const hay = [
          r.asset_name, r.asset_code, r.category_code, r.subcategory,
          r.location, r.source_collection, r.snapshot_item_id,
          r.exit_head, r.price_status, r.vendor, r.vendor_name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // iter220 — sortable columns
    if (sortBy) {
      const dir = sortDir === 'asc' ? 1 : -1;
      const getKey = (r) => {
        const sq = Number(r.quantity_system ?? 0);
        const pq = r.quantity_physical == null ? null : Number(r.quantity_physical);
        switch (sortBy) {
          case 'system':    return sq;
          case 'physical':  return pq == null ? -Infinity : pq;
          case 'diff':      return pq == null ? -Infinity : (pq - sq);
          case 'exit_rate': return Number(r.exit_unit_rate ?? r.effective_unit_rate ?? -Infinity);
          case 'value':     return Number(r.exit_value ?? r.provisional_value ?? -Infinity);
          case 'item':      return (r.asset_name || '').toLowerCase();
          case 'category':  return (r.exit_head || '') + '_' + (r.subcategory || '');
          default:          return 0;
        }
      };
      arr = [...arr].sort((a, b) => {
        const va = getKey(a), vb = getKey(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return  1 * dir;
        return 0;
      });
    }
    return arr;
  }, [rows, query, quickFilter, categoryFilter, qtyFilter, sortBy, sortDir,
       showOnlyBlockers, blockerSids]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  // counters (over ALL rows, not just filtered — user asked for global counts)
  const counters = useMemo(() => {
    let matched = 0, short = 0, excess = 0, pending = 0, verified = 0, damaged = 0;
    let zeroStock = 0, positiveStock = 0;
    for (const r of rows) {
      const sq = Number(r.quantity_system ?? 0);
      const pq = r.quantity_physical == null ? null : Number(r.quantity_physical);
      if (pq == null) { pending++; continue; }
      verified++;
      if (pq === 0) zeroStock++;
      else if (pq > 0) positiveStock++;
      const diff = pq - sq;
      if (Math.abs(diff) < 1e-6) matched++;
      else if (diff < 0) short++;
      else excess++;
      if (CONDITIONS_NEEDS_REMARK.has(r.condition_physical || '')) damaged++;
    }
    return { total: rows.length, verified, matched, short, excess, pending,
              damaged, zeroStock, positiveStock,
              blockers: blockerSids.length };
  }, [rows, blockerSids]);

  // Row visibility grouped by head for the sticky category summary bar.
  const byHead = useMemo(() => {
    const grouped = {};
    for (const r of filtered) {
      (grouped[r.exit_head] = grouped[r.exit_head] || []).push(r);
    }
    return grouped;
  }, [filtered]);

  const headsOrdered = useMemo(() => {
    const seen = new Set();
    const arr = [];
    for (const r of filtered) if (!seen.has(r.exit_head)) { seen.add(r.exit_head); arr.push(r.exit_head); }
    return arr;
  }, [filtered]);

  // —— bulk actions ————————————————————————————
  const bulkPatch = async (patch, opts = {}) => {
    const targetSids = opts.allFiltered
      ? filtered.map(r => r.snapshot_item_id)
      : Array.from(selected);
    if (targetSids.length === 0) { toast.error('Select at least one row'); return; }
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/bulk-upsert/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, snapshot_item_ids: targetSids, patch, reason: opts.reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Bulk update failed');
      toast.success(`${d.updated} row(s) updated`);
      setSelected(new Set());
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  const bulkCopySystemQty = (allFiltered) =>
    bulkPatch({ quantity_physical: '__COPY_SYSTEM__' }, { allFiltered, reason: 'copy system → physical' });

  // —— keyboard: Enter moves focus to next Physical Qty ——
  const focusNextQty = (currentSid) => {
    const order = filtered.map(r => r.snapshot_item_id);
    const idx = order.indexOf(currentSid);
    const nextSid = order[idx + 1];
    if (nextSid && qtyRefs.current[nextSid]) qtyRefs.current[nextSid].focus();
  };

  // —— exit rate override ————————————————————————
  const openPriceEditor = (row) => {
    if (!canOverrideRate) {
      toast.error('Only Super Admin / Admin / Accounts can set an Exit Unit Rate');
      return;
    }
    setPriceEditRow({
      sid: row.snapshot_item_id,
      asset_name: row.asset_name,
      unit: row.unit,
      current_rate: row.effective_unit_rate,
      rate: row.exit_unit_rate || '',
      reason_kind: '',
      reason_other: '',
    });
  };
  const savePriceEditor = async () => {
    const p = priceEditRow; if (!p) return;
    const numRate = Number(p.rate);
    if (!Number.isFinite(numRate) || numRate < 0) { toast.error('Enter a valid Exit Rate'); return; }
    const reason = p.reason_kind === 'Other' ? p.reason_other.trim() : p.reason_kind;
    if (!reason) { toast.error('Reason is required for Exit Rate override'); return; }
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/upsert-row/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, snapshot_item_id: p.sid,
          exit_unit_rate: numRate, exit_rate_reason: reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success('Exit Unit Rate saved (historical accounting untouched)');
      setPriceEditRow(null);
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  // —— finalize ——————————————————————————————————
  // iter221 — quantity-based finalise only. Rate / value are optional.
  const doFinalize = async (force = false) => {
    setFinalizing(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/finalize/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, force }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Finalize failed');
      if (d.success === false) {
        // Only remaining soft-block: unverified AUTO rows (quantity_physical still null).
        if (window.confirm(`${d.message}\n\nProceed with force finalization?`)) {
          return doFinalize(true);
        }
        return;
      }
      toast.success('Asset handover finalized');
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(e.message); }
    finally { setFinalizing(false); }
  };

  // iter220 — Remove Zero Physical Qty (bulk soft-delete for this snapshot only)
  const removeZeroQty = async () => {
    const ok = window.confirm(
      'Remove all rows with Physical Qty = 0 from this Exit Handover reconciliation?\n\n' +
      'This will NOT delete historical inventory or consumption records.');
    if (!ok) return;
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/bulk-remove-zero-qty/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason: 'ZERO PHYSICAL QUANTITY CLEANUP' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Remove failed');
      toast.success(`${d.removed} zero-qty row(s) excluded from this handover`);
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  // iter220 — Bulk Remove Selected (soft-delete only this snapshot)
  const removeSelected = async () => {
    if (selected.size === 0) { toast.error('Select at least one row'); return; }
    const reason = window.prompt(
      `Remove ${selected.size} row(s) from this Exit Handover reconciliation?\n\n` +
      'These rows will NOT be deleted from Inventory / Consumption / Item Master. ' +
      'Reason:', 'Exit handover cleanup');
    if (reason === null) return;
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/bulk-upsert/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, snapshot_item_ids: Array.from(selected),
                               patch: { deleted: true }, reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Remove failed');
      toast.success(`${d.updated} row(s) removed from this handover`);
      setSelected(new Set());
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  // iter220 — Bulk Set Exit Rate (three source modes)
  const submitBulkRate = async () => {
    if (selected.size === 0) { toast.error('Select at least one row'); return; }
    const reason = (bulkRate.reason || '').trim();
    if (!reason) { toast.error('Reason is required'); return; }
    const body = { token, snapshot_item_ids: Array.from(selected),
                    source: bulkRate.source, reason };
    if (bulkRate.source === 'SOURCE_MANUAL') {
      const rate = Number(bulkRate.rate);
      if (!Number.isFinite(rate) || rate < 0) {
        toast.error('Enter a valid manual rate'); return;
      }
      body.rate = rate;
    }
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/bulk-set-rate/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Bulk rate failed');
      toast.success(`Updated ${d.updated} row(s)`
        + (d.skipped?.length ? ` · ${d.skipped.length} skipped (no master rate)` : ''));
      setBulkRateOpen(false);
      setBulkRate({ source: 'SOURCE_CURRENT', rate: '', reason: '' });
      setSelected(new Set());
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  const doRefresh = async () => {
    const reason = window.prompt('Refresh from Current Data will pull in newly-created items and refresh System Qty. Physical qty / condition / remarks / acceptance are preserved.', 'pre-handover refresh');
    if (reason === null) return;
    setRefreshing(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/refresh/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Refresh failed');
      toast.success(`Refreshed — ${d.added} new · ${d.refreshed} updated · ${d.unchanged} unchanged`);
      await fetchList();
    } catch (e) { toast.error(e.message); }
    finally { setRefreshing(false); }
  };

  const deleteRow = async (row) => {
    const reason = window.prompt(`Delete "${row.asset_name}" from handover?  Reason:`, '');
    if (reason === null) return;
    try {
      const r = await fetch(`${API}/api/franchise-exit/asset-snapshot/delete-row/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, snapshot_item_id: row.snapshot_item_id, reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Delete failed');
      toast.success('Row removed');
      await fetchList();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) {
    return <div className="py-16 text-center text-slate-400 flex items-center justify-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading handover snapshot…
    </div>;
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Top toolbar ─────────────────────────────── */}
      <div className="border-b pb-2 space-y-2 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
            <Input placeholder="Search item / category / location / source / asset id"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="pl-7 h-8 text-xs"
              data-testid="ah-search-input" />
          </div>
          <Button size="sm" variant="outline" onClick={doRefresh} disabled={refreshing}
            data-testid="ah-refresh-btn" className="h-8 text-xs">
            {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => setReviewOpen(true)}
            data-testid="ah-review-btn" className="h-8 text-xs">
            <Eye className="w-3 h-3 mr-1" /> Review
          </Button>
          <Button size="sm" onClick={() => doFinalize(false)} disabled={finalizing}
            data-testid="ah-finalize-btn" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700">
            {finalizing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
            Finalize
          </Button>
        </div>

        {/* Quick filters + category chips */}
        <div className="flex flex-wrap gap-1.5 items-center">
          {['ALL','PENDING','VERIFIED','MATCHED','DIFFERENCE','PRICE','PRICE_MISSING','PRICE_MISMATCH','MANUAL_PRICE'].map(k => (
            <button key={k} onClick={() => setQuickFilter(k)}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${quickFilter === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid={`ah-quick-${k}`}>{k.replace('_',' ')}</button>
          ))}
          <span className="mx-1 text-slate-300">|</span>
          {['ALL','FOOD','KITCHEN','FURNITURE','PACKAGING','OTHER'].map(k => (
            <button key={k} onClick={() => setCategoryFilter(k)}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${categoryFilter === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid={`ah-cat-${k}`}>{k}</button>
          ))}
        </div>

        {/* iter220 — Physical Qty filter chips */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] text-slate-500 font-semibold">Physical Qty:</span>
          {[['ALL','All'],['ZERO','Zero'],['GT_ZERO','> 0'],['DIFFERENCE','Difference'],['SHORT','Short'],['EXCESS','Excess'],['MATCHED','Matched']].map(([k, label]) => (
            <button key={k} onClick={() => setQtyFilter(k)}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${qtyFilter === k ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid={`ah-qtyf-${k}`}>{label}</button>
          ))}
          <span className="mx-1 text-slate-300">|</span>
          <Button size="sm" variant="outline"
            className="h-6 text-[10px] border-amber-400 text-amber-800 hover:bg-amber-50"
            onClick={removeZeroQty}
            data-testid="ah-remove-zero-qty">
            <Trash2 className="w-3 h-3 mr-1" />
            Remove Zero Physical Qty
          </Button>
        </div>

        {/* iter221 — rate is no longer a finalise blocker; banner removed. */}

        {/* Counters */}
        <div className="flex flex-wrap gap-3 text-[11px]">
          <span><span className="font-semibold">{counters.total}</span> total</span>
          <span className="text-emerald-600">✓ <span className="font-semibold">{counters.verified}</span> verified</span>
          <span className="text-emerald-500">• <span className="font-semibold">{counters.matched}</span> matched</span>
          <span className="text-red-600">↓ <span className="font-semibold">{counters.short}</span> short</span>
          <span className="text-blue-600">↑ <span className="font-semibold">{counters.excess}</span> excess</span>
          <span className="text-amber-600">◔ <span className="font-semibold">{counters.pending}</span> pending</span>
          <span className="text-orange-600">⚠ <span className="font-semibold">{counters.damaged}</span> damaged/missing</span>
          <span className="mx-1 text-slate-300">|</span>
          {/* iter220 — zero-stock / positive-stock / rate-missing tiles */}
          <button className="text-slate-600 hover:underline"
            onClick={() => setQtyFilter('ZERO')}
            data-testid="ah-tile-zero">
            ⊘ <span className="font-semibold">{counters.zeroStock}</span> zero stock
          </button>
          <button className="text-slate-800 hover:underline"
            onClick={() => setQtyFilter('GT_ZERO')}
            data-testid="ah-tile-positive">
            ● <span className="font-semibold">{counters.positiveStock}</span> positive
          </button>
          {(totals.price_missing || 0) > 0 && (
            <span className="text-slate-500" data-testid="ah-tile-rate-info">
              ⓘ <span className="font-semibold">{totals.price_missing}</span> rate info missing (optional)
            </span>
          )}
          <span className="mx-1 text-slate-300">|</span>
          <span className="text-red-600">₹ <span className="font-semibold">{totals.price_missing || 0}</span> missing</span>
          <span className="text-amber-600"><span className="font-semibold">{totals.price_mismatch || 0}</span> mismatch</span>
          <span className="text-blue-600"><span className="font-semibold">{totals.price_manual || 0}</span> manual</span>
          <span className="ml-auto text-slate-500">Physical value <span className="font-semibold">{inr(totals.physical_asset_value)}</span></span>
        </div>

        {/* Bulk action bar (only when selection non-empty OR filtered scope useful) */}
        {(selected.size > 0 || filtered.length !== rows.length) && (
          <div className="flex items-center gap-1.5 flex-wrap bg-slate-50 border rounded p-1.5 text-[11px]">
            <span className="font-semibold text-slate-700 px-1">
              {selected.size > 0 ? `${selected.size} selected` : `${filtered.length} in view`}
            </span>
            {selected.size > 0 && (
              <>
                <Button size="sm" variant="outline" className="h-6 text-[10px]"
                  onClick={() => bulkCopySystemQty(false)} data-testid="ah-bulk-copy-selected">
                  <Copy className="w-3 h-3 mr-0.5" /> Copy System Qty
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px]"
                  onClick={() => bulkPatch({ condition_physical: 'Good' })} data-testid="ah-bulk-condition-good">
                  Condition: Good
                </Button>
                {canOverrideRate && (
                  <Button size="sm" variant="outline"
                    className="h-6 text-[10px] border-blue-400 text-blue-700 hover:bg-blue-50"
                    onClick={() => setBulkRateOpen(true)}
                    data-testid="ah-bulk-set-rate">
                    Set Exit Rate
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-6 text-[10px]"
                  onClick={() => bulkPatch({ accepted_by_franchisee: true })} data-testid="ah-bulk-accept">
                  Accept
                </Button>
                <Button size="sm" variant="outline"
                  className="h-6 text-[10px] border-red-400 text-red-700 hover:bg-red-50"
                  onClick={removeSelected}
                  data-testid="ah-bulk-remove-selected">
                  <Trash2 className="w-3 h-3 mr-0.5" /> Remove Selected
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                  onClick={() => setSelected(new Set())}>Clear</Button>
              </>
            )}
            <span className="mx-1 text-slate-300">|</span>
            <Button size="sm" variant="outline" className="h-6 text-[10px]"
              onClick={() => setSelected(new Set(filtered.map(r => r.snapshot_item_id)))}
              data-testid="ah-select-all-filtered">
              Select All Filtered
            </Button>
            <Button size="sm" variant="outline" className="h-6 text-[10px] text-blue-700"
              onClick={() => bulkCopySystemQty(true)} data-testid="ah-bulk-copy-filtered">
              <ArrowRightLeft className="w-3 h-3 mr-0.5" /> Copy System → Physical (all filtered)
            </Button>
          </div>
        )}
      </div>

      {/* ── Data table ────────────────────────────── */}
      <div className="flex-1 overflow-y-auto mt-1">
        <table className="w-full text-[11px] table-fixed">
          <thead className="sticky top-0 bg-slate-100 border-b z-10">
            <tr className="text-slate-600">
              <th className="w-8 p-1"><Checkbox
                checked={selected.size > 0 && selected.size === filtered.length}
                onCheckedChange={(v) => setSelected(v ? new Set(filtered.map(r => r.snapshot_item_id)) : new Set())}
                data-testid="ah-select-all-checkbox" /></th>
              <th className="text-left p-1 w-[22%] cursor-pointer select-none" onClick={() => toggleSort('item')}
                  data-testid="ah-sort-item">Item {sortBy === 'item' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
              <th className="text-left p-1 w-[10%] cursor-pointer select-none" onClick={() => toggleSort('category')}
                  data-testid="ah-sort-category">Category {sortBy === 'category' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
              <th className="text-right p-1 w-[9%] cursor-pointer select-none" onClick={() => toggleSort('system')}
                  data-testid="ah-sort-system">System {sortBy === 'system' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
              <th className="text-right p-1 w-[9%] cursor-pointer select-none" onClick={() => toggleSort('physical')}
                  data-testid="ah-sort-physical">Physical {sortBy === 'physical' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
              <th className="text-right p-1 w-[7%] cursor-pointer select-none" onClick={() => toggleSort('diff')}
                  data-testid="ah-sort-diff">Diff {sortBy === 'diff' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
              <th className="text-left p-1 w-[10%]">Condition</th>
              <th className="text-right p-1 w-[9%] cursor-pointer select-none" onClick={() => toggleSort('exit_rate')}
                  data-testid="ah-sort-rate">Exit Rate {sortBy === 'exit_rate' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
              <th className="text-right p-1 w-[8%] cursor-pointer select-none" onClick={() => toggleSort('value')}
                  data-testid="ah-sort-value">Value {sortBy === 'value' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
              <th className="text-left p-1 w-[13%]">Remarks</th>
              <th className="text-center p-1 w-[5%]">Accept</th>
              <th className="text-center p-1 w-[3%]"></th>
            </tr>
          </thead>
          <tbody>
            {headsOrdered.flatMap(head => {
              const meta = EXIT_HEAD_META[head] || { label: head, icon: '📦' };
              const isCollapsed = collapsed[head];
              const list = byHead[head] || [];
              return [
                <tr key={`h-${head}`} className="bg-slate-50 border-t border-slate-200">
                  <td colSpan={12} className="p-1">
                    <button onClick={() => setCollapsed(p => ({ ...p, [head]: !p[head] }))}
                      className="flex items-center gap-1 text-[11px] text-slate-700 font-semibold"
                      data-testid={`ah-head-toggle-${head}`}>
                      {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      <span>{meta.icon}</span> {meta.label}
                      <Badge variant="outline" className="text-[9px] px-1 h-4">{list.length}</Badge>
                    </button>
                  </td>
                </tr>,
                ...(isCollapsed ? [] : list.map(row => renderRow(row))),
              ];
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="p-6 text-center text-slate-400">No rows match the current filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t pt-2 mt-1 text-[10px] text-slate-400 italic flex-shrink-0 flex items-center gap-2">
        Rows auto-populated from Inventory & Consumption.  Physical qty / condition / remarks / acceptance preserved across Refresh.  Every write is audited.
        <Button variant="outline" size="sm" onClick={onClose} className="ml-auto h-7 text-xs">Close</Button>
      </div>

      {/* iter220 — Bulk Set Exit Rate dialog */}
      <Dialog open={bulkRateOpen} onOpenChange={setBulkRateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Set Exit Rate — {selected.size} row(s)</DialogTitle>
            <DialogDescription>
              Applies to selected positive-quantity food-inventory rows. Historical Consumption / WAP are never modified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div>
              <Label className="text-[10px]">Rate Source</Label>
              <Select value={bulkRate.source}
                onValueChange={(v) => setBulkRate(p => ({ ...p, source: v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="ah-bulk-rate-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SOURCE_CURRENT">Use Current Inventory Rate</SelectItem>
                  <SelectItem value="SOURCE_WAP">Use WAP / Latest Purchase Rate</SelectItem>
                  <SelectItem value="SOURCE_MANUAL">Manual Rate ₹…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bulkRate.source === 'SOURCE_MANUAL' && (
              <div>
                <Label className="text-[10px]">Manual Rate (₹)</Label>
                <Input type="number" step="0.01" value={bulkRate.rate}
                  onChange={(e) => setBulkRate(p => ({ ...p, rate: e.target.value }))}
                  className="h-8 text-xs" autoFocus data-testid="ah-bulk-rate-manual" />
              </div>
            )}
            <div>
              <Label className="text-[10px]">Reason *</Label>
              <Input placeholder="e.g. Bulk rate correction from latest purchase invoice"
                value={bulkRate.reason}
                onChange={(e) => setBulkRate(p => ({ ...p, reason: e.target.value }))}
                className="h-8 text-xs" data-testid="ah-bulk-rate-reason" />
            </div>
            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Rows without a current master rate are skipped in SOURCE_CURRENT/WAP mode. Switch to SOURCE_MANUAL to force a value.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setBulkRateOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submitBulkRate} data-testid="ah-bulk-rate-save">
              Apply to {selected.size} row(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Exit-Rate editor ───────────────────────── */}
      <Dialog open={!!priceEditRow} onOpenChange={(o) => !o && setPriceEditRow(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exit Unit Rate — {priceEditRow?.asset_name}</DialogTitle>
            <DialogDescription>
              Override the master rate for THIS exit only.  Historical Consumption / Food Cost / WAP are NOT modified.
            </DialogDescription>
          </DialogHeader>
          {priceEditRow && (
            <div className="space-y-3 text-xs">
              <div className="text-slate-600">
                Current master rate: <span className="font-mono">{priceEditRow.current_rate == null ? 'MISSING' : inr(priceEditRow.current_rate)}</span> per {priceEditRow.unit || 'unit'}
              </div>
              <div>
                <Label className="text-[10px]">Exit Unit Rate (₹)</Label>
                <Input type="number" step="0.01" value={priceEditRow.rate}
                  onChange={(e) => setPriceEditRow(p => ({ ...p, rate: e.target.value }))}
                  className="h-8 text-xs" autoFocus data-testid="ah-price-rate" />
              </div>
              <div>
                <Label className="text-[10px]">Reason</Label>
                <Select value={priceEditRow.reason_kind}
                  onValueChange={(v) => setPriceEditRow(p => ({ ...p, reason_kind: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="ah-price-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                  <SelectContent>
                    {RATE_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {priceEditRow.reason_kind === 'Other' && (
                <Input placeholder="Details" value={priceEditRow.reason_other}
                  onChange={(e) => setPriceEditRow(p => ({ ...p, reason_other: e.target.value }))}
                  className="h-8 text-xs" />
              )}
              <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                This rate applies ONLY to this exit's Asset Handover valuation.  Old accounting rates remain unchanged.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPriceEditRow(null)}>Cancel</Button>
            <Button size="sm" onClick={savePriceEditor} data-testid="ah-price-save">Save Exit Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Review before finalize ─────────────────── */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Handover Review</DialogTitle>
            <DialogDescription>Summary + exceptions.  Fix any hard-blockers before Finalize.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {[
              ['Total', counters.total, 'text-slate-700'],
              ['Verified', counters.verified, 'text-emerald-600'],
              ['Matched', counters.matched, 'text-emerald-500'],
              ['Short', counters.short, 'text-red-600'],
              ['Excess', counters.excess, 'text-blue-600'],
              ['Pending', counters.pending, 'text-amber-600'],
              ['Damaged/Missing', counters.damaged, 'text-orange-600'],
              ['Price Missing', totals.price_missing || 0, 'text-red-600'],
              ['Price Manual', totals.price_manual || 0, 'text-blue-600'],
            ].map(([label, val, cls]) => (
              <div key={label} className="border rounded p-2">
                <div className="text-[10px] text-slate-500">{label}</div>
                <div className={`text-lg font-semibold ${cls}`}>{val}</div>
              </div>
            ))}
          </div>
          <div className="text-xs mt-2 space-y-1">
            <div className="flex gap-3">
              <span>System Value: <strong>{inr(totals.system_asset_value)}</strong></span>
              <span>Physical Value: <strong>{inr(totals.physical_asset_value)}</strong></span>
              <span>Manual rate rows: <strong>{totals.manual_price_adjustments || 0}</strong></span>
            </div>
          </div>
          <div className="mt-2 max-h-72 overflow-y-auto border rounded">
            <table className="w-full text-[10px]">
              <thead className="bg-slate-100 sticky top-0"><tr>
                <th className="text-left p-1">Item</th><th className="text-right p-1">Sys</th>
                <th className="text-right p-1">Phys</th><th className="text-right p-1">Diff</th>
                <th className="text-left p-1">Condition</th><th className="text-left p-1">Remark</th>
                <th className="text-right p-1">Value</th>
              </tr></thead>
              <tbody>
                {rows.filter(r => {
                  const pq = r.quantity_physical == null ? null : Number(r.quantity_physical);
                  const sq = Number(r.quantity_system || 0);
                  return pq == null || Math.abs(pq - sq) > 1e-6
                     || CONDITIONS_NEEDS_REMARK.has(r.condition_physical || '')
                     || r.price_status === 'MISSING';
                }).slice(0, 200).map(r => {
                  const pq = r.quantity_physical == null ? '—' : r.quantity_physical;
                  const diff = r.quantity_physical == null ? '—' : (Number(r.quantity_physical) - Number(r.quantity_system || 0));
                  return <tr key={r.snapshot_item_id} className="border-t">
                    <td className="p-1">{r.asset_name}</td>
                    <td className="p-1 text-right">{r.quantity_system}</td>
                    <td className="p-1 text-right">{pq}</td>
                    <td className="p-1 text-right">{typeof diff === 'number' ? (diff > 0 ? `+${diff}` : diff) : diff}</td>
                    <td className="p-1">{r.condition_physical || ''}</td>
                    <td className="p-1 truncate max-w-[100px]">{r.remarks || ''}</td>
                    <td className="p-1 text-right">{inr(r.exit_value)}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setReviewOpen(false)}>Close</Button>
            <Button size="sm" onClick={() => { setReviewOpen(false); doFinalize(false); }}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="ah-review-finalize">
              Finalize Handover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // — inner row renderer, defined here so it can close over the many state variables above —
  function renderRow(row) {
    const sid = row.snapshot_item_id;
    const sq = Number(row.quantity_system ?? 0);
    const pq = row.quantity_physical == null ? null : Number(row.quantity_physical);
    const diff = pq == null ? null : pq - sq;
    const cond = row.condition_physical || '';
    const isFood = (row.category_code || '').toUpperCase() === 'RAW_MATERIAL';
    const badCondition = CONDITIONS_NEEDS_REMARK.has(cond);
    const needsRemark = ((pq != null && Math.abs(diff) > 1e-6) || badCondition) && !(row.remarks || '').trim();
    const priceIssue = isFood && (row.price_status === 'MISSING' || row.price_status === 'MISMATCH');
    const rowCls = [
      'border-t border-slate-100 text-[11px]',
      needsRemark ? 'bg-red-50' : (pq != null && Math.abs(diff) < 1e-6 ? '' : (pq != null ? 'bg-amber-50/40' : '')),
      // iter221 — price issues no longer paint the row red; rate is optional.
    ].join(' ');
    return (
      <tr key={sid} className={rowCls} data-testid={`ah-row-${sid}`}>
        <td className="p-1 text-center">
          <Checkbox checked={selected.has(sid)}
            onCheckedChange={(v) => setSelected(p => { const n = new Set(p); if (v) n.add(sid); else n.delete(sid); return n; })}
            data-testid={`ah-select-${sid}`} />
        </td>
        <td className="p-1 truncate">
          <div className="font-medium text-slate-700 truncate flex items-center gap-1">
            {row.asset_name}
            {row.source_tag === 'MANUAL_EXIT_ENTRY' && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-blue-100 text-blue-700 border-blue-200">MANUAL</Badge>}
            {row.source_tag === 'AUTO_CONSUMPTION' && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-emerald-100 text-emerald-700 border-emerald-200">FOOD</Badge>}
          </div>
          <div className="text-[9px] text-slate-400 truncate">{row.subcategory || row.category_code || '—'}{row.location ? ` · ${row.location}` : ''}</div>
        </td>
        <td className="p-1 text-[10px] text-slate-500">{(EXIT_HEAD_META[row.exit_head] || {}).label || row.exit_head}</td>
        <td className="p-1 text-right"><span>{row.quantity_system ?? '—'}</span> <span className="text-[9px] text-slate-400">{row.unit || ''}</span></td>
        <td className="p-1">
          <Input ref={(el) => { qtyRefs.current[sid] = el; }}
            type="number" step="0.001"
            className="h-6 text-right text-[11px] px-1"
            value={row.quantity_physical == null ? '' : row.quantity_physical}
            onChange={(e) => patchRow(sid, 'quantity_physical', e.target.value === '' ? null : Number(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusNextQty(sid); } }}
            data-testid={`ah-qty-${sid}`} />
        </td>
        <td className="p-1 text-right">
          {pq == null ? <span className="text-slate-300">—</span>
            : Math.abs(diff) < 1e-6 ? <span className="text-emerald-600 font-semibold">MATCH</span>
            : <span className={diff > 0 ? 'text-blue-600 font-semibold' : 'text-red-600 font-semibold'}>{diff > 0 ? `+${diff}` : diff}</span>}
        </td>
        <td className="p-1">
          <Select value={cond} onValueChange={(v) => patchRow(sid, 'condition_physical', v)}>
            <SelectTrigger className="h-6 text-[10px] px-1" data-testid={`ah-cond-${sid}`}>
              <SelectValue placeholder={row.condition_system || '—'} />
            </SelectTrigger>
            <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </td>
        <td className="p-1 text-right">
          {isFood ? (
            (pq !== null && pq === 0) ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600"
                    data-testid={`ah-rate-zero-${sid}`}>
                ZERO STOCK
              </span>
            ) : (
              <button
                onClick={() => openPriceEditor(row)}
                className={`text-[10px] underline ${row.price_status === 'MANUAL' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`ah-rate-btn-${sid}`}>
                {row.exit_unit_rate != null
                  ? `₹${row.exit_unit_rate} (MANUAL)`
                  : row.effective_unit_rate
                    ? `₹${row.effective_unit_rate}`
                    : 'set (optional)'}
              </button>
            )
          ) : <span className="text-slate-300">—</span>}
        </td>
        <td className="p-1 text-right">
          {row.exit_value != null ? <strong>{inr(row.exit_value)}</strong>
            : row.provisional_value != null ? <span className="text-slate-500 italic">{inr(row.provisional_value)}*</span>
            : '—'}
        </td>
        <td className="p-1">
          <Input placeholder={needsRemark ? 'REQUIRED' : 'remark'}
            list={`quick-remarks-${sid}`}
            className={`h-6 text-[10px] px-1 ${needsRemark ? 'border-red-400' : ''}`}
            value={row.remarks || ''}
            onChange={(e) => patchRow(sid, 'remarks', e.target.value)}
            data-testid={`ah-remarks-${sid}`} />
          <datalist id={`quick-remarks-${sid}`}>
            {QUICK_REMARKS.map(q => <option key={q} value={q} />)}
          </datalist>
        </td>
        <td className="p-1 text-center">
          <Checkbox checked={!!row.accepted_by_franchisee}
            onCheckedChange={(v) => patchRow(sid, 'accepted_by_franchisee', !!v)}
            data-testid={`ah-accept-${sid}`} />
        </td>
        <td className="p-1 text-center">
          {savingRow[sid] && <Loader2 className="w-3 h-3 animate-spin text-slate-400 inline" />}
          {!savingRow[sid] && savedRow[sid] && Date.now() - savedRow[sid] < 2000 && <CheckCircle className="w-3 h-3 text-emerald-500 inline" />}
          <button onClick={() => deleteRow(row)} className="text-slate-300 hover:text-red-500 ml-1" data-testid={`ah-delete-${sid}`}>
            <Trash2 className="w-3 h-3 inline" />
          </button>
        </td>
      </tr>
    );
  }
}
